import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'libs/workroom/features/auth/src/lib/services/auth.service';
import { MessageService } from 'primeng/api';
import { filter, mergeMap, of, switchMap, tap } from 'rxjs';

import { ErpService, ErpSideService } from '@offconon/core-api';
import * as StripeCardPayActions from '@offconon/shared/features/payment/stripe/stripe-card-pay';

import * as BusinessActions from './business.actions';

//import { getSelectedBusiness } from './business.selectors';

@Injectable()
export class BusinessEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly erpService = inject(ErpService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly erpSideService = inject(ErpSideService);
  private readonly messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  verifyBusiness$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BusinessActions.verifyBusiness),
      switchMap(() => {
        return this.erpService
          .erpVerifyEntryCreate({
            erp_name: window.location.host.split('.')[0],
          })
          .pipe(
            tap((data) => {
              if (!data.has_access || data.is_deleted) {
                this.authService.logout();
              }
              if (!data.is_active) {
                this.router.navigate(['/business-management']);
              }
              this.store.dispatch(BusinessActions.updateVerifyBusiness({ verifyData: data }));
            }),
            filter((data) => data?.has_access),
            switchMap((verifyResponse) => {
              return of(
                BusinessActions.getBusinessById({
                  businessId: verifyResponse.business_id,
                }),
              );
            }),
          );
      }),
    );
  });

  getBusinessById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BusinessActions.getBusinessById),
      switchMap(({ businessId }) =>
        this.erpService.erpUserBusinessesList(1, 1, { business__id: businessId }).pipe(
          filter((data) => !!data?.results?.[0]),
          mergeMap((business) => [
            BusinessActions.getBusinessByIdSuccess({ business: business?.results?.[0]! }),
            BusinessActions.updateVerifyBusiness({
              verifyData: { is_active: business?.results?.[0]?.business_is_active },
            }),
          ]),
        ),
      ),
    );
  });

  setBusinessState$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BusinessActions.setBusinessState),
      switchMap(({ businessProfileRequestRequest }) =>
        this.erpSideService.erpSideBusinessEntitySettingsCreate(businessProfileRequestRequest).pipe(
          tap((business) => {
            if (business?.business_active) {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('Successful'),
                detail: this.translateService.instant('Your data has been successfully saved.'),
              });
            }
          }),
          mergeMap((business) => [
            BusinessActions.updateSelectedBusiness({
              business: { business_is_active: business?.business_active },
            }),
            BusinessActions.updateVerifyBusiness({
              verifyData: { is_active: business?.business_active },
            }),
          ]),
        ),
      ),
    );
  });

  updateBusinessEntitySettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BusinessActions.updateBusinessEntitySettings),
      switchMap(({ businessProfileRequestRequest }) =>
        this.erpSideService.erpSideBusinessEntitySettingsCreate(businessProfileRequestRequest).pipe(
          tap(() => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('Successful'),
              detail: this.translateService.instant('Your data has been successfully saved.'),
            });
          }),
          mergeMap((business) => [
            BusinessActions.updateSelectedBusiness({
              business: {
                business_is_active: business?.business_active,
                business_mention_name: business?.business_mention_name,
                business_name: business?.business_name,
                business_avatar_path: business.cropped_avatar?.file_path,
              },
            }),
            BusinessActions.updateVerifyBusiness({
              verifyData: { is_active: business?.business_active },
            }),
          ]),
        ),
      ),
    );
  });

  successFulSubscription$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StripeCardPayActions.confirmSubscriptionCardPaymentSuccess),
      mergeMap((response) => {
        return [
          BusinessActions.updateSelectedBusiness({
            business: {
              business_is_active: response.result.business?.business_active,
              subscription_type: response.result.business?.subscription_type,
              is_subscription: response.result.business?.is_subscription,
              business_is_verified: response.result.business?.business_is_verified,
              subscription_icon: response.result.business?.subscription_icon,
              subscription_icon_color: response.result.business?.subscription_icon_color,
            },
          }),
          BusinessActions.updateVerifyBusiness({
            verifyData: { is_active: response.result.business?.business_active },
          }),
        ];
      }),
    );
  });
}
